import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUnit } from 'effector-react';
import { $isPayinTraderActive } from '~/entities/payin';
import { queryKeys } from '~/shared/api/queryKeys';
import {
    getTraderPayinOrderServiceCountOrdersUrl,
    traderPayinOrderServiceCountOrders,
} from '~/generated/payinBackend/order';
import { TraderPayinOrderServiceCountOrdersFilterStatusesItem } from '~/generated/payinBackend/model';
import { setPayinNewOrdersCount } from '../model';
import { REFETCH_INTERVAL } from './constants';
import { notifyOfNewOrders } from './helper';

const LS_TOTAL_KEY = 'payin_prev_orders_total_count';
const LS_WAITING_KEY = 'payin_prev_orders_waiting_count';

interface OrdersCountSnapshot {
    total: number;
    waiting: number;
}

const ordersTotalQuery = {};

const ordersWaitingQuery = {
    'filter.statuses': [TraderPayinOrderServiceCountOrdersFilterStatusesItem.STATUS_MERCHANT_CONFIRMED],
};

const readPersistedCount = (): OrdersCountSnapshot | null => {
    const rawTotal = localStorage.getItem(LS_TOTAL_KEY);
    const rawWaiting = localStorage.getItem(LS_WAITING_KEY);

    if (rawTotal === null || rawWaiting === null) {
        return null;
    }

    const total = Number(rawTotal);
    const waiting = Number(rawWaiting);

    if (!Number.isFinite(total) || !Number.isFinite(waiting)) {
        return null;
    }

    return {
        total,
        waiting,
    };
};

const persistCount = ({ total, waiting }: OrdersCountSnapshot) => {
    localStorage.setItem(LS_TOTAL_KEY, String(total));
    localStorage.setItem(LS_WAITING_KEY, String(waiting));
};

export const useWatchPayinOrdersCount = () => {
    const isTraderActive = useUnit($isPayinTraderActive);
    const queryClient = useQueryClient();
    const prevCountRef = useRef<OrdersCountSnapshot | null>(readPersistedCount());

    const totalOrdersQuery = useQuery({
        enabled: isTraderActive,
        refetchInterval: REFETCH_INTERVAL,
        refetchOnWindowFocus: true,
        queryFn: async () => await traderPayinOrderServiceCountOrders(ordersTotalQuery),
        queryKey: [queryKeys.PAYIN_ORDERS_COUNT, getTraderPayinOrderServiceCountOrdersUrl(ordersTotalQuery), 'total'],
    });

    const waitingOrdersQuery = useQuery({
        enabled: isTraderActive,
        refetchInterval: REFETCH_INTERVAL,
        refetchOnWindowFocus: true,
        queryFn: async () => await traderPayinOrderServiceCountOrders(ordersWaitingQuery),
        queryKey: [
            queryKeys.PAYIN_ORDERS_COUNT,
            getTraderPayinOrderServiceCountOrdersUrl(ordersWaitingQuery),
            'waiting',
        ],
    });

    useEffect(() => {
        if (!isTraderActive) {
            prevCountRef.current = null;
            setPayinNewOrdersCount(0);

            return;
        }

        if (!totalOrdersQuery.isSuccess || !waitingOrdersQuery.isSuccess) {
            return;
        }

        const total = Number(totalOrdersQuery.data?.pagination?.total) || 0;
        const waiting = Number(waitingOrdersQuery.data?.pagination?.total) || 0;
        const prevCount = prevCountRef.current;
        const currentCount = {
            total,
            waiting,
        };

        prevCountRef.current = currentCount;
        persistCount(currentCount);
        setPayinNewOrdersCount(waiting);

        if (prevCount && total > prevCount.total && waiting !== prevCount.waiting) {
            void notifyOfNewOrders();
            void queryClient.invalidateQueries({
                queryKey: [queryKeys.PAYIN_ORDERS],
            });
        }
    }, [
        isTraderActive,
        queryClient,
        totalOrdersQuery.data?.pagination?.total,
        totalOrdersQuery.isSuccess,
        waitingOrdersQuery.data?.pagination?.total,
        waitingOrdersQuery.isSuccess,
    ]);
};
